/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { classNames } from '../utils/UNSAFE_classNames';
import { styles } from './themes/PlotAreaStyles.css';
import { Scale } from '../utils/UNSAFE_visTypes/chart';
import { styles as chartStyles } from '#PRIVATE_Chart/themes/ChartStyles.css';

type ConstantLineRefObjProps = {
  id: string;
  xScale: Scale;
  yScale: Scale;
  value: number;
  orientation?: 'horizontal' | 'vertical';
  lineColor?: string;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  lineWidth?: number;
  accessibleLabel?: string;
  activeId?: string;
  isXRef?: boolean;
};

export function ConstantLineRefObj({
  id,
  xScale,
  yScale,
  value,
  orientation = 'horizontal',
  lineColor,
  lineWidth,
  lineStyle,
  accessibleLabel,
  isXRef,
  activeId
}: ConstantLineRefObjProps) {
  if (value === undefined || !xScale || !yScale) {
    return null;
  }

  const targetScale = isXRef ? xScale : yScale;
  const spanningScale = isXRef ? yScale : xScale;
  const pos = targetScale.transform(value);
  const [span1, span2] = spanningScale.range();

  if (typeof pos !== 'number' || isNaN(pos)) {
    return null;
  }

  // Determine horizontal or vertical line
  const isHorizontalLine =
    (orientation === 'vertical' && !isXRef) || (orientation === 'horizontal' && isXRef);

  const path = isHorizontalLine
    ? `M${span1},${pos}L${span2},${pos}` // Horizontal line
    : `M${pos},${span1}L${pos},${span2}`; // Vertical line

  const contrastPath = isHorizontalLine
    ? `M${span1},${pos + 1}L${span2},${pos + 1}` // Horizontal line
    : `M${pos + 1},${span1}L${pos + 1},${span2}`; // Vertical line

  return (
    <g>
      <path
        id={activeId}
        d={path}
        aria-label={accessibleLabel}
        data-oj-private-ref-obj-id={id}
        data-oj-private-object={'refLine'}
        role="img"
        shape-rendering="crispEdges"
        className={classNames([
          lineStyle === 'dashed' ? styles.dashedLine : undefined,
          lineStyle === 'dotted' ? styles.dottedLine : undefined,
          chartStyles.refLine
        ])}
        style={{
          stroke: lineColor,
          strokeWidth: lineWidth
        }}
      />
      <path
        id={activeId}
        d={contrastPath}
        data-oj-private-ref-obj-id={id}
        data-oj-private-object={'refLine'}
        shape-rendering="crispEdges"
        className={classNames([
          lineStyle === 'dashed' ? styles.dashedLine : undefined,
          lineStyle === 'dotted' ? styles.dottedLine : undefined,
          chartStyles.contrastBorder
        ])}
        style={{
          stroke: lineColor,
          strokeWidth: lineWidth
        }}
      />
    </g>
  );
}
